import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { FixedSizeList as List } from 'react-window';

const OPTION_HEIGHT = 40;
const MENU_MAX_HEIGHT = 300;
const BATCH_SIZE = 50;

const SingleSelectDropdown = ({
   label,
   options = [],
   value = null, // Changed from array to single value
   onChange,
   placeholder = 'Select option...',
   isRequired = false,
   error,
   showAddButton = true,
   onAddNew,
   isLoading = false,
   darkMode = false,
   AddEditFormComponent,
   formProps = {},
   ...props
}) => {
   const [isOpen, setIsOpen] = useState(false);
   const [searchTerm, setSearchTerm] = useState('');
   const [loadedCount, setLoadedCount] = useState(BATCH_SIZE);
   const [isFormOpen, setIsFormOpen] = useState(false);
   const [isEditMode, setIsEditMode] = useState(false);
   const [currentRow, setCurrentRow] = useState({});
   const [demoname, setDemoname] = useState('');
   const selectRef = useRef(null);
   const searchRef = useRef(null);
   const listRef = useRef(null);

   // Mock user context - remove this and use actual useUser hook
   const user = { isExpired: false };

   // Theme-based class generator
   const getThemeClass = (lightClass, darkClass) => {
      return darkMode ? darkClass : lightClass;
   };

   const useData = options.map((opt) => ({
      ...opt,
      value: opt.id,
      label: opt.label ?? opt.name,
   }));

   // Filter useData based on search term (with trimmed search)
   const filteredOptions = useMemo(() => {
      const trimmedSearchTerm = searchTerm.trim();
      if (!trimmedSearchTerm) return useData;

      return useData.filter(
         (option) =>
            option.label?.toLowerCase().includes(trimmedSearchTerm.toLowerCase()) ||
            option.value?.toString().toLowerCase().includes(trimmedSearchTerm.toLowerCase()) ||
            option.gstRegNo?.toLowerCase().includes(trimmedSearchTerm.toLowerCase()) ||
            option.panNo?.toLowerCase().includes(trimmedSearchTerm.toLowerCase()) ||
            option.mobile?.toLowerCase().includes(trimmedSearchTerm.toLowerCase()) ||
            option.ownerName?.toLowerCase().includes(trimmedSearchTerm.toLowerCase()),
      );
   }, [useData, searchTerm]);

   // useData to display (with virtual scrolling)
   const displayOptions = useMemo(() => {
      return filteredOptions.slice(0, loadedCount);
   }, [filteredOptions, loadedCount]);

   const selectedOption = useMemo(() => {
      return useData.find((opt) => opt.value === value);
   }, [value, useData]);

   // Handle individual option selection
   const handleOptionSelect = useCallback(
      (optionValue) => {
         onChange(optionValue);
         setIsOpen(false); // Close dropdown after selection
         setSearchTerm(''); // Clear search term
      },
      [onChange],
   );

   const handleClear = useCallback(
      (e) => {
         e.stopPropagation();
         onChange(null);
      },
      [onChange],
   );

   // Load more items on scroll
   const handleScroll = useCallback(
      ({ scrollOffset, scrollUpdateWasRequested }) => {
         if (
            !scrollUpdateWasRequested &&
            loadedCount < filteredOptions.length &&
            scrollOffset > loadedCount * OPTION_HEIGHT - MENU_MAX_HEIGHT - 100
         ) {
            setLoadedCount((prev) => Math.min(prev + BATCH_SIZE, filteredOptions.length));
         }
      },
      [loadedCount, filteredOptions.length],
   );

   // Reset loaded count when search changes
   useEffect(() => {
      setLoadedCount(BATCH_SIZE);
   }, [searchTerm]);

   // Close dropdown when clicking outside
   useEffect(() => {
      const handleClickOutside = (event) => {
         if (selectRef.current && !selectRef.current.contains(event.target)) {
            setIsOpen(false);
            setSearchTerm('');
         }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
   }, []);

   // Focus search when opening
   useEffect(() => {
      if (isOpen && searchRef.current) {
         setTimeout(() => searchRef.current?.focus(), 100);
      }
   }, [isOpen]);

   const handleAddNew = useCallback(
      (searchValue = '') => {
         const trimmedSearchValue = searchValue.trim();
         setDemoname(trimmedSearchValue);
         setIsEditMode(false);
         setCurrentRow({});
         setIsFormOpen(true);
         setIsOpen(false);
         setSearchTerm('');

         if (onAddNew) {
            onAddNew(trimmedSearchValue);
         }
      },
      [onAddNew],
   );

   const handleFormSave = useCallback(
      (formData) => {
         setIsFormOpen(false);

         // Create new option from form data
         const newOption = {
            ...formData,
            label: formData.name || formData.label,
            value: formData.id,
         };

         // Set the new option as selected
         onChange(newOption.value);

         // Reopen the dropdown
         setTimeout(() => {
            setIsOpen(true);
         }, 100);
      },
      [onChange],
   );

   const handleFormClose = useCallback(() => {
      setIsFormOpen(false);

      // Reopen the dropdown when form is closed without saving
      setTimeout(() => {
         setIsOpen(true);
      }, 100);
   }, []);

   const Option = useCallback(
      ({ index, style, data }) => {
         const option = data[index];
         const isSelected = value === option.value;

         return (
            <div
               style={style}
               className={`
            flex items-center gap-3 px-4 py-2 cursor-pointer transition-colors
            ${isSelected
                     ? getThemeClass('bg-blue-50', 'bg-blue-900/30')
                     : getThemeClass('hover:bg-gray-50', 'hover:bg-gray-700')
                  }
            ${getThemeClass('text-gray-900', 'text-gray-100')}
          `}
               onClick={() => handleOptionSelect(option.value)}>
               <span className="truncate flex-1">{option.label}</span>
            </div>
         );
      },
      [value, handleOptionSelect, darkMode],
   );

   return (
      <>
         <div className="relative w-full" ref={selectRef}>
            {/* Label */}
            <label className={`block text-sm font-medium mb-1 ${getThemeClass('text-gray-700', 'text-gray-300')}`}>
               {label}
               {isRequired && <span className="text-red-500 ml-1">*</span>}
            </label>

            {/* Trigger Button */}
            <button
               type="button"
               className={`
            w-full px-4 py-2.5 text-left rounded-lg border transition-all
            flex items-center justify-between gap-2
            ${error
                     ? 'border-red-500 focus:ring-2 focus:ring-red-200'
                     : getThemeClass(
                        'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200',
                        'border-gray-600 focus:border-blue-400 focus:ring-2 focus:ring-blue-800'
                     )
                  }
            ${getThemeClass('bg-white text-gray-900', 'bg-gray-800 text-gray-100')}
            ${isOpen ? 'ring-2' : ''}
          `}
               onClick={() => setIsOpen(!isOpen)}>
               <span className={`truncate flex-1 ${!value ? getThemeClass('text-gray-400', 'text-gray-500') : ''}`}>
                  {value ? selectedOption?.label : placeholder}
               </span>

               <div className="flex items-center gap-1 flex-shrink-0">
                  {value && (
                     <button
                        type="button"
                        onClick={handleClear}
                        className={`p-1 rounded transition-colors ${darkMode ? "hover:bg-gray-600" : "hover:bg-gray-300"}`}>
                        <svg className="w-4 h-4" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                           <path d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                     </button>
                  )}
                  <svg
                     className={`w-5 h-5 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
                     fill="none"
                     strokeLinecap="round"
                     strokeLinejoin="round"
                     strokeWidth="2"
                     viewBox="0 0 24 24"
                     stroke="currentColor">
                     <path d="M19 9l-7 7-7-7"></path>
                  </svg>
               </div>
            </button>

            {/* Error Message */}
            {error && <p className="mt-1 text-sm text-red-500">{error}</p>}

            {/* Dropdown Menu */}
            {isOpen && (
               <div
                  className={`
              absolute z-50 w-full mt-1 rounded-lg shadow-lg border overflow-hidden
              ${getThemeClass('bg-white border-gray-200', 'bg-gray-800 border-gray-700')}
            `}
                  style={{ maxHeight: MENU_MAX_HEIGHT + 100 }}>
                  {/* Search Input */}
                  <div className={`p-3 border-b ${getThemeClass('border-gray-200', 'border-gray-700')}`}>
                     <div className="relative">
                        <input
                           ref={searchRef}
                           type="text"
                           placeholder="Search..."
                           className={`
                    w-full pl-9 pr-9 py-2 rounded-md border transition-colors
                    ${getThemeClass(
                              'border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200',
                              'border-gray-600 bg-gray-700 text-gray-100 placeholder-gray-500 focus:border-blue-400 focus:ring-2 focus:ring-blue-800'
                           )}
                  `}
                           value={searchTerm}
                           onChange={(e) => setSearchTerm(e.target.value)}
                           onClick={(e) => e.stopPropagation()}
                        />
                        <svg
                           className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${getThemeClass('text-gray-400', 'text-gray-500')}`}
                           fill="none"
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth="2"
                           viewBox="0 0 24 24"
                           stroke="currentColor">
                           <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                        </svg>
                        {searchTerm && (
                           <button
                              type="button"
                              className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${getThemeClass('text-gray-400 hover:text-gray-600', 'text-gray-500 hover:text-gray-300')}`}
                              onClick={() => setSearchTerm('')}>
                              <svg className="w-4 h-4" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                 <path d="M6 18L18 6M6 6l12 12"></path>
                              </svg>
                           </button>
                        )}
                     </div>
                  </div>

                  {/* Options List */}
                  <div style={{ maxHeight: MENU_MAX_HEIGHT, overflow: 'auto' }}>
                     {isLoading ? (
                        <div className="flex items-center justify-center py-8">
                           <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                     ) : displayOptions.length === 0 ? (
                        // No options found
                        <div className="py-8 text-center">
                           <p className={`text-sm ${getThemeClass('text-gray-500', 'text-gray-400')}`}>
                              {searchTerm.trim() ? `No options found for "${searchTerm.trim()}"` : 'No options available'}
                           </p>
                           {showAddButton && onAddNew && searchTerm.trim() && user && !user.isExpired && (
                              <button
                                 type="button"
                                 className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                                 onClick={() => {
                                    handleAddNew(searchTerm);
                                    onAddNew(searchTerm.trim());
                                    setIsOpen(false);
                                    setSearchTerm('');
                                 }}>
                                 Add New {label}
                              </button>
                           )}
                        </div>
                     ) : (
                        // Virtualized list
                        <List
                           ref={listRef}
                           height={Math.min(displayOptions.length * OPTION_HEIGHT, MENU_MAX_HEIGHT)}
                           itemCount={displayOptions.length}
                           itemSize={OPTION_HEIGHT}
                           width="100%"
                           itemData={displayOptions}
                           onScroll={handleScroll}>
                           {Option}
                        </List>
                     )}
                  </div>

                  {/* Footer with selection info */}
                  {!isLoading && displayOptions.length > 0 && (
                     <div className={`px-4 py-2 text-xs border-t ${getThemeClass('border-gray-200 text-gray-500 bg-gray-50', 'border-gray-700 text-gray-400 bg-gray-900')}`}>
                        {value ? '1 selected' : 'No selection'}
                        {searchTerm && ` (filtered from ${useData.length})`}
                        {loadedCount < filteredOptions.length && (
                           <span className="ml-2">
                              • Showing {loadedCount} of {filteredOptions.length}
                           </span>
                        )}
                     </div>
                  )}
               </div>
            )}
         </div>

         {/* Add/Edit Form Modal */}
         {AddEditFormComponent && isFormOpen && (
            <AddEditFormComponent
               isOpen={isFormOpen}
               onClose={handleFormClose}
               onSave={handleFormSave}
               isEditMode={isEditMode}
               currentRow={currentRow}
               demoname={demoname}
               {...formProps}
            />
         )}
      </>
   );
};

export default SingleSelectDropdown;